import {Component, DestroyRef, inject, Inject, OnInit} from '@angular/core';
import { FormControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FileUploadDialogComponent } from '@emm/shared/dialogs/file-upload-dialog/file-upload-dialog.component';
import { PinOverrideGroup } from '@emm/core/models/pin-override-group.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { PinOverride } from '@emm/core/models/pin-override.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {MenuDateUtil} from '@emm/core/util/menu-date.util';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {LocationSet} from '@emm/core/models/location-set.model';



@Component({
    selector: 'emm-add-pin-overrides-dialog',
    styleUrls: ['./add-pin-overrides-dialog.component.scss'],
    templateUrl: './add-pin-overrides-dialog.component.html'
})
export class AddPinOverridesDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    firstFormGroup: FormGroup;
    secondFormGroup: FormGroup;
    thirdFormGroup: FormGroup;
    fourthFormGroup: FormGroup;

    minDate = new Date();
    noEndDate: boolean;

    newPinOverrides: PinOverride[] = [];
    newPinOverride: PinOverride;

    displayedColumns: string[] = ['RMI', 'Primary', 'Secondary'];
    fields: [] = [];
    field;
    fileArray: [] = [];

    rmi: number;
    primaryOverride: number;
    secondaryOverride: number;
    wholeProductOverride: number;
    samplingDistributionOverride: number;

    areHeadersVisible = false;
    areFileTableHeadersVisible = false;
    fileUploaded = false;

    childLocationSets$ = this.data.childLocationSets$;
    isLoading$ = this.data.isLoading$;
    isLocationSetTypeParent$ = this.data.isLocationSetTypeParent$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    parentLabel$ = this.data.parentLabel$;
    parentLocationSets$ = this.data.parentLocationSets$;
    selectedLocationSetType$ = this.data.selectedLocationSetType$;
    selectedLocationSet$ = this.data.selectedLocationSet$;
    selectedLocationSetType;

    locationTag;

    constructor(
        private authFacade: AuthFacadeService,
        public dialogRef: MatDialogRef<AddPinOverridesDialogComponent>,
        private fb: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any) {}

    public ngOnInit() {
        this.selectedLocationSetType$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => this.selectedLocationSetType = result.tag);
        this.dialogRef.updatePosition({ top: '100px' });

        this.firstFormGroup = this.fb.group({
            name: new FormControl('', Validators.required),
            description: new FormControl(''),
        });
        this.secondFormGroup = this.fb.group({
            effectiveDate: new FormControl('', Validators.required),
            endDate: new FormControl(''),
            noEndDate: new FormControl(false),
        }, {
            validators: [endDateConditionallyRequiredValidator]
        });
        this.thirdFormGroup = this.fb.group({
            location: new FormControl(''),
        });
        this.fourthFormGroup = this.fb.group({}); // this needs to exist because of the matStepper component
    }

    onNoClick(): void {
        this.dialogRef.close();
    }

    onSubmit(): void {
        this.closeAfterSubmit();
    }

    closeAfterSubmit(): void {
        this.getInputs();
        const pinOverrideGroup: PinOverrideGroup = {
            id: null,
            location: this.locationTag,
            name: this.firstFormGroup.controls.name.value,
            description: this.firstFormGroup.controls.description.value,
            effectiveDate: this.secondFormGroup.controls.effectiveDate.value,
            endDate: this.secondFormGroup.controls.endDate.value,
            pinOverrides: this.newPinOverrides.filter(result => result.rmi > 0)
        };
        this.dialogRef.close(pinOverrideGroup);
    }

    selectEffectiveDate(date: string) {
        this.secondFormGroup.get('effectiveDate').setValue(MenuDateUtil.formatDateToMidnight(date));
    }

    selectEndDate(date: string) {
        this.secondFormGroup.get('endDate').setValue(MenuDateUtil.formatDateToMidnight(date));
    }

    selectNoEndDate(event: MatSlideToggleChange) {
        this.noEndDate = event.checked;
        if (this.noEndDate) {
            this.secondFormGroup.get('endDate').setValue(null);
        }
    }

    deleteRow() {
        const index = this.fields.length - 1;
        this.fields.splice(index, 1);
    }

    deleteFileRow() {
        const index = this.fileArray.length - 1;
        this.fileArray.splice(index, 1);
    }


    getInputs() {
        // This function grabs location and override input values to POST to database
        this.selectedLocationSet$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((result) => {
            this.locationTag = result.tag.toString();
            return this.locationTag;
        });

        if (this.fileUploaded === true) {
            for (const override of this.fileArray) {
                this.rmi = override['rmi'];
                this.primaryOverride = override['primaryOverride'];
                this.secondaryOverride = override['secondaryOverride'];
                this.wholeProductOverride = override['wholeProductOverride'];
                this.samplingDistributionOverride = override['samplingDistributionOverride'];

                this.newPinOverride = {
                    id: null,
                    rmi: this.rmi,
                    primaryOverride: this.primaryOverride,
                    secondaryOverride: this.secondaryOverride,
                    wholeProductOverride: this.wholeProductOverride,
                    samplingDistributionOverride: this.samplingDistributionOverride,
                };
                this.newPinOverrides.push(this.newPinOverride);
            }
        } else {
            for (const field of this.fields) {
                this.rmi = field['rmi'];
                this.primaryOverride = field['primaryOverride'];
                this.secondaryOverride = field['secondaryOverride'];
                this.wholeProductOverride = field['wholeProductOverride'];
                this.samplingDistributionOverride = field['samplingDistributionOverride'];

                this.newPinOverride = {
                    id: null,
                    rmi: this.rmi,
                    primaryOverride: this.primaryOverride,
                    secondaryOverride: this.secondaryOverride,
                    wholeProductOverride: this.wholeProductOverride,
                    samplingDistributionOverride: this.samplingDistributionOverride,
                };
                this.newPinOverrides.push(this.newPinOverride);
            }
        }

        for (const pinOverride of this.newPinOverrides) {
            if (pinOverride.primaryOverride === 0) {
                pinOverride.primaryOverride = null;
            }
            if (pinOverride.secondaryOverride === 0) {
                pinOverride.secondaryOverride = null;
            }
            if (pinOverride.wholeProductOverride === 0) {
                pinOverride.wholeProductOverride = null;
            }
            if (pinOverride.samplingDistributionOverride === 0) {
                pinOverride.samplingDistributionOverride = null;
            }
        }
    }

    handleFileUpload(): void {
        const fileUploadRef = this.data.dialog.open(FileUploadDialogComponent, {
            width: '350px',
            height: 'auto',
            data: {
                dialog: this.data.dialog,
            }
        });
        fileUploadRef
            .afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                this.fileArray = result.fileArray;

                if (this.fileArray.length >= 1) {
                    this.areHeadersVisible = false;
                    this.areFileTableHeadersVisible = true;
                    this.fileUploaded = true;
                }
            });
    }

    addRow() {
        // This function creates a new row in Overrides table
        if (this.fileArray.length >= 1) {
            this.fileUploaded = true;
        }
        if (this.fileUploaded === false) {
            this.areHeadersVisible = true;
            this.field = {
                rmi: null,
                primaryOverride: null,
                secondaryOverride: null,
                wholeProductOverride: null,
                samplingDistributionOverride: null,
            };
            // @ts-ignore
            this.fields.unshift(this.field);
            this.field = {};
        } else {
            this.addFileRows();
        }
    }

    addFileRows() {
        this.areFileTableHeadersVisible = true;
        this.field = {
            rmi: null,
            primaryOverride: null,
            secondaryOverride: null,
            wholeProductOverride: null,
            samplingDistributionOverride: null,
        };
        // @ts-ignore
        this.fileArray.unshift(this.field);
    }

    locationSetTypeSelected(selected: LocationSetType): void {
        this.data.managementFacade.selectLocationSetType(selected.tag);
    }

    locationSetSelected(selected: LocationSet): void {
        this.data.managementFacade.selectLocationSet(selected.tag);
    }

    resetSelectedLocationSet(): void {
        this.data.managementFacade.selectLocationSet(null);
    }

    filterLocationSets(locationSetFilter: string): void {
        this.data.managementFacade.applyLocationSetFilter(locationSetFilter);
    }

    parentLocationSetSelected(selected: LocationSet): void {
        this.data.managementFacade.selectParentLocationSet(selected.tag);
    }
}

function endDateConditionallyRequiredValidator(formGroup: FormGroup) {
    if (!formGroup.value.noEndDate) {
        return Validators.required(formGroup.get('endDate')) ? {
            myEndDateFieldConditionallyRequired: true,
        } : null;
    }
    return null;
}
